import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { showDialog } from "@widgets/CommonDialog";
import SnButton from "@shared/components/SnButton";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnSearchBtn from "@shared/components/SnSearchBtn";
import SnResizeBar from "@shared/components/SnResizeBar";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnForm, { ValueChangedEvent } from "@widgets/SnForm";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const SN_PRD_WMO_REG_001_CHEM_POP_001 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref: React.Ref<any>) => {
    //#region variable
    const { userInfo } = global();
    const searchRef = useRef<any>(null);
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
    });
    const [popupParent, setPopupParent] = useState<any>(null);
    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
    });
    //#endregion

    useImperativeHandle(ref, () => ({
      getSelectedRow: () => [
        gridRefs.current["grid1"].current?.getSelectedRows() || [],
        gridRefs.current["grid2"].current?.getSelectedRows() || [],
      ],
    }));

    useEffect(() => {
      if (modalRef?.current) {
        setPopupParent(modalRef.current.getPopupParent());
      }
    }, [modalRef?.current]);

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_PRD_WMO_REG_001_CHEM_POP_001",
            {}
          );
          setFormControlData(rtn.formControlData);
          const mergedData: any = { ...rtn.formData, ...selectedData };
          setFormData(mergedData);
          setColums({
            grid1: rtn.grid.grid1,
            grid2: rtn.grid.grid2,
          });
          await new Promise((resolve) => setTimeout(resolve, 0));
          await onSearch();
        } catch (error: any) {}
      };
      fetchform();
    }, []);

    const isInitRef = useRef(false);

    useEffect(() => {
      if (!selectedData?.dt) return;
      if (!formData) return;
      if (isInitRef.current) return;

      isInitRef.current = true;

      const fetchWeek = async () => {
        const params = {
          id: "sn_prd_mre_reg_001_chem_03",
          params: {
            cd_firm: userInfo.cd_firm,
            dt: selectedData.dt,
          },
        };

        const weekData: any = await fnGetDataSet(params);
        const row = weekData?.[0]?.[0] || {};

        setFormData((prev: any) => ({
          ...(prev || {}),
          dt: selectedData.dt,
          week: row.week || "",
          dt_from: row.dt_from || "",
          dt_to: row.dt_to || "",
        }));
      };

      fetchWeek();
    }, [selectedData?.dt, formData]);
    //#region Button Event
    const onSearch = async () => {
      try {
        const params = {
          id: "sn_prd_wmo_reg_001_chem_pop_001",
          params: searchRef.current?.getFormdata() ?? {},
        };
        gridRefs.current["grid1"].current?.resetUpdate();
        gridRefs.current["grid2"].current?.resetUpdate();
        const data: any = await fnGetDataSet(params);
        setDataSources({
          grid1: data[0] || [],
          grid2: data[1] || [],
        });
      } catch (error: any) {
        showDialog("error", error.message);
      }
    };

    //#endregion
    const DateValueChanged = async (event: ValueChangedEvent) => {
      const colId = event.colId;
      const value = event.newValue;

      if (colId.includes("dt")) {
        try {
          const params = {
            id: "sn_prd_mre_reg_001_chem_03",
            params: {
              cd_firm: userInfo.cd_firm,
              dt: value,
            },
          };

          const weekData: any = await fnGetDataSet(params);
          const row = weekData?.[0]?.[0] || {};

          return {
            [colId]: value,
            week: row.week || "",
            dt_from: row.dt_from || "",
            dt_to: row.dt_to || "",
          };
        } catch (e) {
          console.error(e);
          return {
            [colId]: value,
          };
        }
      }
      return {
        [colId]: value,
      };
    };
    const onCellValueChanged = async (event: CellValueChangedEvent) => {};
    //#region grid events
    const onFocusedRowChanged = (event: any) => {
      const selectedRow = event;
      if (!selectedRow) return;
      gridRefs.current["grid2"].current?.applyFilters({
        no_plan: selectedRow.no_plan,
      });
      gridRefs.current["grid2"].current?.selectAndFocusRow(0);
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onFocusedRowChanged: onFocusedRowChanged,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
        },
      }),
      [columns, dataSources]
    );
    //#endregion

    return (
      <div className="w-full h-full flex flex-col min-h-0">
        <div className="mb-1">
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
            onValueChanged={DateValueChanged}
          />
        </div>
        <div style={{ textAlign: "center" }}>
          <SnSearchBtn
            text={"조회"}
            onClick={onSearch}
            sx={{ width: 80, marginTop: 1.5 }}
            spinner={true}
          />
        </div>
        <div className="flex flex-col flex-1 min-h-0 gap-1.5 mt-2">
          <SnResizeBar direction={"height"} id="panel1" orgSize="50%">
            <SnGrid
              ref={gridRefs.current["grid1"]}
              columnConfig={grids.grid1.columns}
              rowData={grids.grid1.dataSource}
              etcProps={{
                rowSelection: "single",
                isEditable: true,
                title: "생산계획",
                popupParent: popupParent,
              }}
              events={grids.grid1.events}
            />
          </SnResizeBar>
          <div className="flex-1 min-h-0">
            <SnGrid
              ref={gridRefs.current["grid2"]}
              columnConfig={grids.grid2.columns}
              rowData={grids.grid2.dataSource}
              etcProps={{
                rowSelection: "single",
                isEditable: true,
                title: "계획정보",
                popupParent: popupParent,
              }}
            />
          </div>
        </div>
      </div>
    );
  }
);

export default SN_PRD_WMO_REG_001_CHEM_POP_001;
